import React, { createContext, useContext, ReactNode } from 'react';
import ApiClient from '../../lib/api-client';
import { IRequisiteCreationDTO } from '@common-frontend-submodule/interfaces/requisite/irequisite-creation.interface';
import { IRequisiteDTO } from '@common-frontend-submodule/interfaces/requisite/irequisite.interface';
import { IApiResponseGenericDTO } from '@common-frontend-submodule/interfaces/common/iapi-response.interface';
import { RequisiteRoutes } from '@common-frontend-submodule/enums/routes/requisite-routes.enum';

const apiClient = ApiClient.getInstance()

interface RequisiteContextProps {
  getById: (id: string) => Promise<IApiResponseGenericDTO<IRequisiteDTO>>
  getAll: () => Promise<IApiResponseGenericDTO<IRequisiteDTO[]>>
  delete: (contractId: string) => Promise<IApiResponseGenericDTO<boolean>>
  create: (file: File, model: IRequisiteCreationDTO) => Promise<IApiResponseGenericDTO<IRequisiteDTO>>
}

const RequisiteContext = createContext<RequisiteContextProps | undefined>(undefined)

export const RequisiteProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const getById = async (id: string): Promise<IApiResponseGenericDTO<IRequisiteDTO>> => {
    return await apiClient.getRequest<IRequisiteDTO>(`${RequisiteRoutes.BasePrefix}/${id}`)
  };

  const getAll = async (): Promise<IApiResponseGenericDTO<IRequisiteDTO[]>> => {
    return await apiClient.getRequest<IRequisiteDTO[]>(`${RequisiteRoutes.BasePrefix}/all`)
  };

  const create = async (file: File, model: IRequisiteCreationDTO): Promise<IApiResponseGenericDTO<IRequisiteDTO>> => {
    const formData = new FormData()

    formData.append('files', file)

    Object.entries(model).forEach(([key, value]) => {
      formData.append(key, value as string)
    });
  
    return await apiClient.postFormDataRequest<FormData, IRequisiteDTO>(
      RequisiteRoutes.BasePrefix,
      formData,
    );
  };

  const deleteRequisiteFile = async (id: string): Promise<IApiResponseGenericDTO<boolean>> => {
    return await apiClient.deleteRequest<null, boolean>(`${RequisiteRoutes.BasePrefix}/${id}`)
  };

  return (
    <RequisiteContext.Provider value={{ getById, getAll, create, delete: deleteRequisiteFile }}>
      {children}
    </RequisiteContext.Provider>
  );
};

export const useRequisiteStore = () => {
  const context = useContext(RequisiteContext);
  if (!context) {
    throw new Error('useRequisiteStore must be used within a RequisiteProvider');
  }
  return context;
};
